// src/Screens/NotFound.jsx
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, GamepadIcon, AlertCircle } from "lucide-react";
import SeoHead from "../Components/SeoHead";

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [roomCode, setRoomCode] = useState("");

  const handleJoinRoom = (e) => {
    e.preventDefault();
    const code = roomCode.trim().toUpperCase();
    if (code) {
      navigate(`/join-room/${code}`);
    }
  };

  return (
    <div className="min-h-screen bg-orange-50 flex flex-col items-center justify-center px-4">
      <SeoHead
        title="Page Not Found | NameIt"
        description="This page doesn't exist or the room link has expired."
        path={location.pathname}
        noindex
      />
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="bg-orange-100 rounded-full p-6">
            <AlertCircle className="w-16 h-16 text-orange-500" />
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-900 mb-3">
          This link doesn't work
        </h1>
        <p className="text-gray-600 mb-8 leading-relaxed">
          The page you're looking for couldn't be found. This usually happens because:
        </p>

        {/* Reasons list */}
        <ul className="text-left bg-white rounded-xl border border-orange-100 shadow-sm p-5 mb-8 space-y-3 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-orange-500 mt-0.5">•</span>
            <span>The game room has already ended or been deleted</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-orange-500 mt-0.5">•</span>
            <span>The link expired — rooms are cleaned up after they finish</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-orange-500 mt-0.5">•</span>
            <span>The room code in the link was typed incorrectly</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-orange-500 mt-0.5">•</span>
            <span>The page doesn't exist</span>
          </li>
        </ul>

        {/* Join by code */}
        <div className="bg-white rounded-xl border border-orange-100 shadow-sm p-5 mb-4">
          <p className="text-sm font-medium text-gray-700 mb-3">
            Have a room code? Enter it here:
          </p>
          <form onSubmit={handleJoinRoom} className="flex gap-2">
            <input
              type="text"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              placeholder="e.g. ABC234"
              maxLength={8}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 uppercase tracking-widest font-mono"
            />
            <button
              type="submit"
              disabled={!roomCode.trim()}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Join
            </button>
          </form>
        </div>

        {/* Go home */}
        <button
          onClick={() => navigate("/")}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
        >
          <Home className="w-4 h-4" />
          Back to Home
        </button>
      </div>
    </div>
  );
};

export default NotFound;
